import React, { useState, useEffect, useCallback } from 'react';
import { Search, RotateCcw, Trash2, ChevronLeft, ChevronRight, Archive, AlertTriangle } from 'lucide-react';
import Toast from '../components/Toast';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function DeletedDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/deliveries/deleted/list?page=${page}&limit=50&search=${encodeURIComponent(search)}`, { credentials: 'include' });
      const data = await res.json();
      setDeliveries(data.data);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function handleRestore(id) {
    if (!window.confirm('Restore this delivery back to the main table?')) return;
    try {
      const res = await fetch(`/api/deliveries/deleted/${id}/restore`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: 'Delivery restored successfully', type: 'success', duration: 3000 });
        load(pagination.page);
      } else {
        setToast({ message: data.error || 'Failed to restore', type: 'error', duration: 5000 });
      }
    } catch (err) {
      setToast({ message: 'Restore failed: ' + err.message, type: 'error', duration: 5000 });
    }
  }

  async function handlePermanentDelete(id) {
    if (!window.confirm('PERMANENTLY delete this record? This cannot be undone!')) return;
    try {
      const res = await fetch(`/api/deliveries/deleted/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: 'Permanently deleted from archive', type: 'info', duration: 3000 });
        load(pagination.page);
      } else {
        setToast({ message: data.error || 'Failed to delete', type: 'error', duration: 5000 });
      }
    } catch (err) {
      setToast({ message: 'Delete failed: ' + err.message, type: 'error', duration: 5000 });
    }
  }

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />}
      <div className="table-container">
        <div className="table-header table-title-row">
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Archive size={20} style={{ color: '#f59e0b' }} />
              Deleted Deliveries ({pagination.total.toLocaleString()} archived)
            </h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>
              Records moved here when deleted from Deliveries. Restore or permanently remove them.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="table-filters-row">
          <div className="table-search-wrap">
            <Search size={15} className="table-search-icon" />
            <input
              className="search-input table-search-input"
              placeholder="Search invoice, customer, dispatch ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Customer Code</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Driver</th>
                <th>Plate #</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Deleted At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
              ) : deliveries.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 60 }}>
                    <Archive size={40} style={{ color: '#cbd5e1', marginBottom: 12 }} />
                    <p style={{ color: '#94a3b8', fontSize: 15 }}>No deleted deliveries</p>
                    <p style={{ color: '#cbd5e1', fontSize: 12, marginTop: 4 }}>Deleted records will appear here</p>
                  </td>
                </tr>
              ) : deliveries.map((d) => (
                <tr key={d.id} style={{ background: '#fffbeb' }}>
                  <td>{formatDate(d.date)}</td>
                  <td>{d.invoice_number || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.customer_code || '-'}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.customer_name || '-'}</td>
                  <td className="amount">{formatCurrency(d.amount)}</td>
                  <td>{d.driver || '-'}</td>
                  <td>{d.plate_number || '-'}</td>
                  <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.destination || '-'}</td>
                  <td><span className="status-badge status-pending">{d.delivery_status || 'PENDING'}</span></td>
                  <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDateTime(d.deleted_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleRestore(d.id)}
                        title="Restore to Deliveries"
                        style={{ background: '#10b981', color: 'white', border: 'none' }}
                      >
                        <RotateCcw size={14} />
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handlePermanentDelete(d.id)}
                        title="Permanently Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} records)
          </div>
          <div className="pagination-buttons">
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(1)}>First</button>
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.totalPages)}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
